import type { LoomVerifiedAircraftTransitionAsset } from "./loomAircraftTransitionAsset";

function continuityLabel(value: "g1-tangent" | "g2-curvature"): string {
  return value === "g2-curvature" ? "Curvature (G2)" : "Tangent (G1)";
}
function metric(value: number | null, unit: string): string {
  return value === null ? "not required for G1" : `${value.toExponential(3)} ${unit}`;
}

export function LoomJunctionContinuityReceipt({ asset }: {
  asset: LoomVerifiedAircraftTransitionAsset | null | undefined;
}) {
  if (!asset || asset.header.version !== 3) return null;
  const receipt = asset.header.transitions[0]?.continuityReceipt;
  if (!receipt) return null;
  const rows = [
    ["Wing contact", receipt.parentA],
    ["Fuselage contact", receipt.parentB],
  ] as const;
  return <section aria-label="Accepted fairing continuity" className="loom-junction-continuity-receipt"
    data-receipt-revision={receipt.receiptRevision} data-testid="loom-junction-continuity-receipt">
    <strong>Accepted fairing continuity</strong>
    <p>Measured by the authoritative geometry build. No requested contact was downgraded.</p>
    {rows.map(([label, row]) => <div data-testid={`loom-continuity-${label.startsWith("Wing") ? "wing" : "fuselage"}`} key={label}>
      <h5>{label}</h5>
      <dl>
        <div><dt>Requested</dt><dd>{continuityLabel(row.requestedContinuity)}</dd></div>
        <div><dt>Achieved</dt><dd>{continuityLabel(row.achievedContinuity)}</dd></div>
        <div><dt>Position gap (G0)</dt><dd>{metric(row.maximumPositionGapM, "m")}</dd></div>
        <div><dt>Tangent-plane error (G1)</dt><dd>{metric(row.maximumTangentPlaneAngleRad, "rad")}</dd></div>
        <div><dt>Curvature mismatch</dt><dd>{metric(row.maximumAbsoluteCurvatureMismatchPerM, "1/m")}</dd></div>
        <div><dt>Relative curvature mismatch</dt><dd>{metric(row.maximumRelativeCurvatureMismatch, "")}</dd></div>
      </dl>
    </div>)}
    <dl>
      <div><dt>Fairness energy</dt><dd>{metric(receipt.fairness.curvatureVariationEnergyPerM3, "1/m³")}</dd></div>
      <div><dt>Inflections</dt><dd>{receipt.fairness.inflectionCount}</dd></div>
      <div><dt>Ripples</dt><dd>{receipt.fairness.rippleCount}</dd></div>
      <div><dt>Samples per station</dt><dd>{receipt.fairness.sampleCountPerStation}</dd></div>
    </dl>
  </section>;
}
